import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiFetch, auth } from '../api'
import { getApiBaseUrl } from '../api'

function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    setError(null)

    if (!token) {
      setError('Missing invitation token.')
      setLoading(false)
      return
    }

    try {
      const data = await apiFetch('/api/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify({
          token,
          password,
          username: username.trim().toLowerCase(),
          fullName: fullName.trim() || undefined
        })
      }, false)

      if (data) {
        const tokens: any = data
        const accessToken = tokens.accessToken
        const refreshToken = tokens.refreshToken

        if (accessToken) {
          localStorage.setItem('cadence_access_token', accessToken)
        }
        if (refreshToken) {
          localStorage.setItem('cadence_refresh_token', refreshToken)
        }

        // trigger parent auth flow
        window.location.href = '/'
        return
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation.')
    }

    setLoading(false)
  }

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand__name">CADENCE</div>
          <div className="auth-brand__sub">by Captus</div>
        </div>
        <h1 className="auth-headline">
          Accept your invitation to Cadence.
        </h1>
        <p className="auth-body">
          Set a password and choose a username to access your bid workspace.
        </p>
      </div>

      <div className="auth-right">
        <div className="auth-form">
          <h2>Accept Invitation</h2>
          <p>Complete your account setup to get started.</p>

          {error && <div className="error-msg">{error}</div>}

          {!token && (
            <div className="error-msg">
              No invitation token found. Please use the link from your invitation email.
            </div>
          )}

          {token && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={username}
                  onChange={event => setUsername(event.target.value.replace(/\s/g, '').toLowerCase())}
                  placeholder="e.g. jonathanp"
                  required
                  minLength={3}
                  maxLength={50}
                  pattern="^[a-z0-9_]+$"
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--slate)', marginTop: '4px' }}>
                  Lowercase letters, numbers, and underscores only. 3–50 characters.
                </div>
              </div>
              <div className="form-group">
                <label>Full Name (optional)</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={event => setFullName(event.target.value)}
                  placeholder="e.g. Jonathan Phillpotts"
                />
              </div>
              <button
                type="submit"
                className="btn btn--primary btn--full"
                disabled={loading}
              >
                {loading ? 'Setting up your account…' : 'Complete Setup'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default AcceptInvite
